<template>
	<div class="select-box" v-click-outside="onClickOutside">
		<div
			tabindex="0"
			ref="selectBox"
			class="select-box__box"
			@blur="toggleOptions(false)"
			@click="toggleOptionsAndMoveFocus"
			@keydown.down.enter.space.prevent="optionsShowAndMoveFocus"
			@keydown.esc.prevent="optionsHideAndFocusSelf"
		>
			<span class="select-box__current">
				{{ getCurrentLabel }}
			</span>
		</div>
		<ul v-if="showOptions" class="select-box__ul" :style="boxStyle">
			<li
				v-for="(option, index) in options"
				:key="option.value"
				tabindex="0"
				ref="option"
				class="select-box__ul__li"
				@focus="toggleOptions(true)"
				@keydown.up.prevent="moveFocus(index - 1)"
				@keydown.down.prevent="moveFocus(index + 1)"
				@keydown.enter.space.prevent="onSelectItem(option.value)"
				@keydown.esc.prevent="optionsHideAndFocusSelf"
				@click="onSelectItem(option.value)"
			>
				<span>{{ option.label }}</span>
			</li>
		</ul>
	</div>
</template>
<script>
	import ClickOutside from 'vue-click-outside';
	import find from 'lodash/find';
	import findIndex from 'lodash/findIndex';

	export default {
		name: 'SelectBoxV2',
		directives: {
			ClickOutside,
		},
		props: {
			options: {
				type: Array,
				default() {
					return [];
				},
				validator(options) {
					let result = true;

					if (options.length > 0) {
						options.forEach(option => {
							if (
								option &&
								typeof option === 'object' &&
								!Array.isArray(option)
							) {
								if (
									!option.hasOwnProperty('value') ||
									!option.hasOwnProperty('label')
								) {
									result = false;
								}
							} else {
								result = false;
							}
						});
					}

					return result;
				},
			},
			value: {
				type: [Number, String, null],
				default: null,
			},
			placeholder: {
				type: String,
				default: '',
			},
			boxStyle: {
				type: Object,
				default: () => {},
			},
		},
		data() {
			return {
				showOptions: false,
			};
		},
		computed: {
			getCurrentLabel() {
				const currentOption = find(this.options, { value: this.value });

				return currentOption ? currentOption.label : this.placeholder;
			},
		},
		methods: {
			toggleOptions(to) {
				if (typeof to === 'boolean') {
					this.showOptions = to;
				} else {
					this.showOptions = !this.showOptions;
				}
			},
			onClickOutside() {
				if (this.showOptions) {
					this.toggleOptions(false);
				}
			},
			onSelectItem(value) {
				this.$refs.selectBox.focus();
				this.$emit('input', value);
				this.toggleOptions(false);
			},
			moveFocus(indexTo) {
				const lastIndex = this.options.length - 1;

				if (indexTo < 0) {
					this.$refs.option[lastIndex].focus();
				} else if (indexTo > lastIndex) {
					this.$refs.option[0].focus();
				} else {
					this.$refs.option[indexTo].focus();
				}
			},
			toggleOptionsAndMoveFocus() {
				if (this.showOptions) {
					this.toggleOptions(false);
				} else {
					this.optionsShowAndMoveFocus();
				}
			},
			optionsShowAndMoveFocus() {
				this.toggleOptions(true);
				this.$nextTick(() => {
					const index = findIndex(this.options, {
						value: this.value,
					});

					if (index > -1) {
						this.moveFocus(index);
					} else {
						this.moveFocus(0);
					}
				});
			},
			optionsHideAndFocusSelf() {
				this.toggleOptions(false);
				this.$refs.selectBox.focus();
			},
		},
	};
</script>
<style lang="scss" scoped>
	$font: #657088;
	$border: #dfe6ee;
	$background: #f4f7fa;

	DIV.select-box {
		position: relative;
		display: inline-block;
		cursor: pointer;
		color: $font;
		font-size: 1rem;
		font-weight: bold;
		min-width: 4em;
		min-height: 36px;

		.select-box__box {
			width: 100%;
			height: 100%;
			padding: 0.5em 1.75em 0.5em 1em;
			background: #fff;
			border-radius: 5px;
			border: 1px solid $border;

			&:focus {
				border-color: $btn-blue;
			}

			&:after {
				content: '';
				position: absolute;
				width: 0.5em;
				height: 0.5em;
				top: 50%;
				right: 0.75em;
				transform: translate3d(0, -125%, 0) rotate(45deg);
				background: #fff;
				box-shadow: 2px 2px 0 0 $font;
			}
		}

		.select-box__current {
			vertical-align: sub;
		}

		.select-box__ul {
			position: absolute;
			top: calc(100% + 1px);
			left: 0;
			background: #fff;
			box-shadow: 2px 2px 4px rgba(#000, 0.5);
			border: 1px solid $border;
			border-radius: 5px;
			z-index: 999;
		}

		.select-box__ul__li {
			padding: 0.5em 1em;
			word-break: keep-all;
			font-weight: normal;

			&:hover,
			&:focus {
				background: $background;
			}
		}
	}
</style>
